import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, Loader2, Search, Wallet, Clock as ClockIcon, BadgeCheck, Package } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';

const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

type SortKey = 'date_desc' | 'date_asc';

// Page dédiée à l'historique complet des commandes fournisseur d'un contact CRM — extraite de la fiche
// contact (qui n'affichait qu'une liste brute, sans recherche ni tri) sur le même modèle que
// CRMContactDemarchesPage.tsx. Le détail d'une commande (paiement, envoi, réponse fournisseur) s'ouvre
// dans une modale au clic, mêmes champs que sur le module Commandes.
export const CRMContactCommandesPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [contact, setContact] = React.useState<any | null>(null);
    const [orders, setOrders] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [sort, setSort] = React.useState<SortKey>('date_desc');
    const [selectedOrder, setSelectedOrder] = React.useState<any | null>(null);

    React.useEffect(() => {
        if (!id) return;
        Promise.all([moduleApi.getCRMContact(id), moduleApi.getContactOrders(id)])
            .then(([c, o]: any[]) => { setContact(c); setOrders(Array.isArray(o) ? o : []); })
            .catch(() => { setContact(null); setOrders([]); })
            .finally(() => setLoading(false));
    }, [id]);

    const contactLabel = contact
        ? (contact.type === 'PROFESSIONNEL' ? contact.company_name : `${contact.first_name || ''} ${contact.last_name || ''}`.trim())
        : '';

    const filtered = React.useMemo(() => {
        const query = q.trim().toLowerCase();
        const list = query
            ? orders.filter((o) => String(o.supplier_name || '').toLowerCase().includes(query) || String(o.template_name || '').toLowerCase().includes(query) || (o.items || []).some((it: any) => String(it.label || '').toLowerCase().includes(query)))
            : orders;
        return [...list].sort((a, b) => {
            const da = new Date(a.created_at || a.ordered_at || 0).getTime();
            const db = new Date(b.created_at || b.ordered_at || 0).getTime();
            return sort === 'date_asc' ? da - db : db - da;
        });
    }, [orders, q, sort]);

    return (
        <div className="space-y-6">
            <header className="flex items-center gap-3">
                <button onClick={() => navigate(`/crm/contacts/${id}`)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all">
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-content flex items-center gap-2"><Truck size={22} /> Historique des commandes</h1>
                    <p className="text-content-muted mt-1">{contactLabel || '...'}</p>
                </div>
            </header>

            <div className="flex items-center gap-2 max-w-2xl">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un fournisseur, une démarche, un article..." className="pl-8" />
                </div>
                <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
                    className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                    <option value="date_desc">Date (récent → ancien)</option>
                    <option value="date_asc">Date (ancien → récent)</option>
                </select>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : filtered.length === 0 ? (
                <p className="text-center text-content-muted py-12 bg-surface rounded-2xl border border-dashed border-border max-w-2xl">Aucune commande trouvée.</p>
            ) : (
                <div className="space-y-3 max-w-2xl">
                    {filtered.map((o) => (
                        <button key={o.order_id} onClick={() => setSelectedOrder(o)}
                            className="w-full flex items-center justify-between gap-3 bg-surface p-4 rounded-2xl border border-border hover:border-brand-500/40 transition-colors text-left">
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-content truncate">{o.supplier_name} <span className="text-content-muted font-normal">— {o.template_name}</span></p>
                                <p className="text-xs text-content-muted mt-0.5">{(o.items || []).map((it: any) => `${it.label} x${it.qty}`).join(' · ')}</p>
                                <p className="text-xs text-content-muted mt-0.5">{fmtDate(o.ordered_at || o.created_at)}</p>
                            </div>
                            <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest shrink-0 ${o.confirmed_at ? 'bg-brand-500/10 text-brand-600 dark:text-brand-400' : o.status === 'commandee' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`}>
                                {o.confirmed_at ? 'Confirmée' : o.status === 'commandee' ? 'Commandée' : 'À commander'}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {selectedOrder && (
                <Modal open onClose={() => setSelectedOrder(null)} title={`Commande — ${selectedOrder.supplier_name}`} icon={<Truck size={16} />} maxWidth="max-w-md"
                    footer={<button onClick={() => setSelectedOrder(null)} className="px-4 py-2 rounded-lg border border-border text-content-muted text-xs font-bold hover:bg-surface-soft">Fermer</button>}>
                    <div className="space-y-3 text-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-content-muted">Démarche</span>
                            <span className="font-bold text-content">{selectedOrder.template_name}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-content-muted">Statut</span>
                            <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest ${selectedOrder.confirmed_at ? 'bg-brand-500/10 text-brand-600 dark:text-brand-400' : selectedOrder.status === 'commandee' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`}>
                                {selectedOrder.confirmed_at ? 'Confirmée' : selectedOrder.status === 'commandee' ? 'Commandée' : 'À commander'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-content-muted flex items-center gap-1.5"><Wallet size={13} /> Paiement</span>
                            <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest ${selectedOrder.payment_status === 'paid' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`}>
                                {selectedOrder.payment_status === 'paid' ? 'Payé' : 'Non payé'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-content-muted">Montant</span>
                            <span className="font-bold text-content">{fmt(selectedOrder.amount_due)}</span>
                        </div>
                        {selectedOrder.paid_at && (
                            <div className="flex items-center justify-between">
                                <span className="text-content-muted">Réglée le</span>
                                <span className="text-content">{fmtDate(selectedOrder.paid_at)}</span>
                            </div>
                        )}
                        {selectedOrder.ordered_at && (
                            <div className="flex items-center justify-between">
                                <span className="text-content-muted flex items-center gap-1.5"><ClockIcon size={13} /> Commandée le</span>
                                <span className="text-content">{fmtDate(selectedOrder.ordered_at)}</span>
                            </div>
                        )}
                        {selectedOrder.confirmed_at && (
                            <div className="flex items-center justify-between">
                                <span className="text-content-muted flex items-center gap-1.5"><BadgeCheck size={13} /> Confirmée le</span>
                                <span className="text-content">{fmtDate(selectedOrder.confirmed_at)}</span>
                            </div>
                        )}
                        {selectedOrder.desired_date && (
                            <div className="flex items-center justify-between">
                                <span className="text-content-muted">Réception souhaitée</span>
                                <span className="text-content">{fmtDate(selectedOrder.desired_date)}</span>
                            </div>
                        )}
                        <div className="pt-2 border-t border-border space-y-1.5">
                            <p className="text-xs font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Package size={13} /> Articles</p>
                            {(selectedOrder.items || []).map((it: any, idx: number) => (
                                <div key={idx} className="flex items-center justify-between px-2.5 py-2 rounded-md bg-surface-soft border border-border">
                                    <span className="text-content">{it.label}</span>
                                    <span className="text-content-muted">x{it.qty}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default CRMContactCommandesPage;
